/* ================================================================
   Texture Cache
   ----------------------------------------------------------------
   Three.js textures are expensive to upload to GPU. Cache them by
   URL and return the same THREE.Texture to every caller. Also
   exposes a synchronous `getCachedTexture` that returns null until
   the async load resolves — BoardMesh uses that + a tiny React
   state bump to trigger a re-render once the image is ready.

   Extensions:
   - `getCachedNormalMap` / `loadNormalMap` derive a normal map from
     the diffuse via Sobel on an offscreen canvas (one-shot, cached).
   - `hashSeed(str)` turns a mesh id into a deterministic [0,1) value
     so per-plank UV offsets stay stable across re-renders.
   ================================================================ */

import * as THREE from 'three';

// --------------- Cache storage ---------------

interface CacheEntry {
  texture: THREE.Texture | null;
  promise: Promise<THREE.Texture>;
  subscribers: Set<() => void>;
}

const cache = new Map<string, CacheEntry>();
const normalCache = new Map<string, THREE.Texture>();
const normalPromises = new Map<string, Promise<THREE.Texture | null>>();
const loader = new THREE.TextureLoader();

// --------------- Public API ---------------

/** Returns the cached texture if already loaded, null otherwise. */
export function getCachedTexture(url: string): THREE.Texture | null {
  return cache.get(url)?.texture ?? null;
}

/** Ensures the texture is loading; resolves when ready. */
export function loadTexture(url: string): Promise<THREE.Texture> {
  let entry = cache.get(url);
  if (entry) return entry.promise;

  const subscribers = new Set<() => void>();
  const promise = new Promise<THREE.Texture>((resolve, reject) => {
    loader.load(
      url,
      texture => {
        // Wood grain should tile seamlessly.
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.RepeatWrapping;
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.anisotropy = 4;
        const e = cache.get(url);
        if (e) {
          e.texture = texture;
          e.subscribers.forEach(fn => fn());
        }
        resolve(texture);

        // Lazily derive a normal map (async, non-blocking).
        void loadNormalMap(url);
      },
      undefined,
      err => {
        console.warn(`[textures] failed to load ${url}`, err);
        reject(err);
      },
    );
  });

  entry = { texture: null, promise, subscribers };
  cache.set(url, entry);
  return promise;
}

// --------------- Normal map generation ---------------

/** Returns the cached normal map if already derived. */
export function getCachedNormalMap(url: string): THREE.Texture | null {
  return normalCache.get(url) ?? null;
}

/**
 * Load (or reuse) a derived normal map for a given diffuse URL.
 * The normal is computed once by running a Sobel filter on the
 * diffuse's luminance channel in an offscreen canvas. The resulting
 * texture is cached globally.
 */
export function loadNormalMap(url: string): Promise<THREE.Texture | null> {
  const existing = normalCache.get(url);
  if (existing) return Promise.resolve(existing);

  const pending = normalPromises.get(url);
  if (pending) return pending;

  const promise = loadTexture(url).then(diffuse => {
    const normal = generateNormalFromDiffuse(diffuse, 2.5);
    if (normal) {
      normalCache.set(url, normal);
      // Notify any diffuse subscribers so planks re-render with the
      // newly available normal map.
      const entry = cache.get(url);
      if (entry) entry.subscribers.forEach(fn => fn());
    }
    return normal;
  }).catch(() => null);

  normalPromises.set(url, promise);
  return promise;
}

/**
 * Sobel-based normal map from a source texture. The source must have
 * its `image` populated (HTMLImageElement or ImageBitmap). Runs on a
 * 2D canvas so it's CPU-only — typical 1024² images finish in <50ms.
 */
export function generateNormalFromDiffuse(
  source: THREE.Texture,
  strength = 2.5,
): THREE.Texture | null {
  const img = source.image as HTMLImageElement | ImageBitmap | HTMLCanvasElement | null;
  if (!img) return null;

  const w = (img as any).naturalWidth ?? (img as any).width;
  const h = (img as any).naturalHeight ?? (img as any).height;
  if (!w || !h) return null;

  // Cap resolution so startup stays snappy.
  const maxDim = 512;
  const scale = Math.min(1, maxDim / Math.max(w, h));
  const cw = Math.max(1, Math.round(w * scale));
  const ch = Math.max(1, Math.round(h * scale));

  let canvas: HTMLCanvasElement;
  try {
    canvas = document.createElement('canvas');
  } catch {
    return null;
  }
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(img as CanvasImageSource, 0, 0, cw, ch);

  let src: ImageData;
  try {
    src = ctx.getImageData(0, 0, cw, ch);
  } catch {
    return null;
  }
  const dst = ctx.createImageData(cw, ch);
  const sdata = src.data;
  const ddata = dst.data;

  // Precompute luminance once.
  const lum = new Float32Array(cw * ch);
  for (let i = 0; i < cw * ch; i++) {
    const idx = i * 4;
    lum[i] = 0.299 * sdata[idx] + 0.587 * sdata[idx + 1] + 0.114 * sdata[idx + 2];
  }

  for (let y = 0; y < ch; y++) {
    const ym = y > 0 ? y - 1 : y;
    const yp = y < ch - 1 ? y + 1 : y;
    for (let x = 0; x < cw; x++) {
      const xm = x > 0 ? x - 1 : x;
      const xp = x < cw - 1 ? x + 1 : x;

      const tl = lum[ym * cw + xm];
      const tc = lum[ym * cw + x];
      const tr = lum[ym * cw + xp];
      const ml = lum[y * cw + xm];
      const mr = lum[y * cw + xp];
      const bl = lum[yp * cw + xm];
      const bc = lum[yp * cw + x];
      const br = lum[yp * cw + xp];

      // Sobel kernels
      const dx = (tr + 2 * mr + br) - (tl + 2 * ml + bl);
      const dy = (bl + 2 * bc + br) - (tl + 2 * tc + tr);

      // Tangent-space normal
      const nx = -dx * strength / 255;
      const ny = -dy * strength / 255;
      const nz = 1;
      const len = Math.sqrt(nx * nx + ny * ny + nz * nz);

      const outIdx = (y * cw + x) * 4;
      ddata[outIdx] = ((nx / len) * 0.5 + 0.5) * 255;
      ddata[outIdx + 1] = ((ny / len) * 0.5 + 0.5) * 255;
      ddata[outIdx + 2] = ((nz / len) * 0.5 + 0.5) * 255;
      ddata[outIdx + 3] = 255;
    }
  }

  ctx.putImageData(dst, 0, 0);

  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.NoColorSpace; // Normal maps are linear
  tex.needsUpdate = true;
  return tex;
}

// --------------- Deterministic per-plank seed ---------------

/** Stable [0,1) hash of a string (djb2). Used for per-plank UV offsets. */
export function hashSeed(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h) ^ s.charCodeAt(i);
  }
  return (h >>> 0) / 0xFFFFFFFF;
}

/**
 * Subscribe to texture availability. The callback fires once as soon
 * as the texture finishes loading (if already cached, it fires
 * synchronously). Returns an unsubscribe function.
 */
export function subscribeTexture(url: string, cb: () => void): () => void {
  let entry = cache.get(url);
  if (!entry) {
    void loadTexture(url);
    entry = cache.get(url)!;
  }
  if (entry.texture) {
    // Already loaded — fire immediately on next tick to avoid sync
    // setState during render.
    queueMicrotask(cb);
    return () => {};
  }
  entry.subscribers.add(cb);
  return () => { entry!.subscribers.delete(cb); };
}

/** Release GPU memory for all textures. Used sparingly (on unmount). */
export function disposeAllTextures(): void {
  for (const entry of cache.values()) {
    entry.texture?.dispose();
  }
  cache.clear();
}

// --------------- Geometry UV helpers ---------------

/**
 * Compute tile repeat for a box geometry face.
 * Given the three-dimensional box size (mm) and a tile size (mm),
 * we want the grain on the LARGEST face to show N = L/tile tiles.
 *
 * The repeat is clamped to [1, 6] so narrow end faces don't turn
 * the wood grain into noise. Callers should clone the shared texture
 * before mutating `texture.repeat`.
 */
export function computeBoxRepeat(
  wMm: number, hMm: number, dMm: number, tileMm: number,
): [number, number] {
  const dims = [wMm, hMm, dMm].sort((a, b) => b - a);
  const major = dims[0];
  const minor = dims[1];
  const tile = Math.max(120, tileMm || 400);
  return [
    Math.max(1, Math.min(6, Math.round(major / tile))),
    Math.max(1, Math.min(4, Math.round(minor / tile))),
  ];
}

/**
 * Compute repeat for a single visible face. Compared with computeBoxRepeat,
 * this keeps more detail on long planks while avoiding noisy tiling on
 * narrow end faces.
 */
export function computeFaceRepeat(
  faceWidthMm: number,
  faceHeightMm: number,
  tileMm: number,
): [number, number] {
  const tile = Math.max(120, tileMm || 400);
  return [
    Math.max(1, Math.min(10, Math.round(faceWidthMm / tile))),
    Math.max(1, Math.min(6, Math.round(faceHeightMm / tile))),
  ];
}
